import { useEffect, useMemo, useRef, useState } from 'react'
import UploadFiles from '../UploadFiles'
import Modal from '../../reusables/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { setStdFilesData, saveStdPriority } from '../../reduxSlices/results/resultsSlice'
import LineChartPreview from '../LineChartPreview'
import { MdOutlineChevronRight } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { logUserEvent } from '../../lib/utilities/logUserEvent';
import BackButton from '../shared/BackButton';
import { resetAll } from '../../reduxSlices/results/resultsSlice';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useConfigNavigationGuard } from '../../hooks/useConfigNavigationGuard';
// import saveResultsToFirebase from '../../lib/utilities/saveResultsToFirebase';
// import  sanitizeKeyDeep  from '../../lib/utilities/sanitizeKeyDeep';

// Generate or retrieve from localStorage
const getOrCreateConfigId = () => {
    const existing = localStorage.getItem('currentConfigId');
    console.log("Existing configId from localStorage:", existing);
    if (existing) return existing;

    const newId = uuidv4();
    localStorage.setItem('currentConfigId', newId);
    return newId;
};

const configId = getOrCreateConfigId();

function UploadStdFiles() {

    // const results = useSelector(state => state.results);
    const [files, setFiles] = useState([]);
    const [fileName, setFileName] = useState('');
    const [open, setOpen] = useState(false);
    const [chartPreviewData, setChartPreviewData] = useState([]);
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const stdFilesObj = useSelector(store => store.results.stdFiles);
    const stdFilesPriority = useSelector(store => store.results.stdFilesPriority);

    // Navigation guard
    const { showExitModal, handleConfirmExit, handleCancelExit } = useConfigNavigationGuard(
        ['/configuration/uploadQcFiles', '/configuration'],
        () => dispatch(resetAll())
    );

    // Build initial list from Redux (preserves saved order)
    const initialStdFiles = useMemo(() => {
        const stdObj = stdFilesObj || {};
        const names = (Array.isArray(stdFilesPriority) && stdFilesPriority.length > 0)
            ? stdFilesPriority
            : Object.keys(stdObj);
        return names
            .map(name => ({ fileName: name, fileData: stdObj[name] }))
            .filter(item => item.fileData)
            .map((item, idx) => ({ ...item, path: item.path || item.fileName }));
    }, [stdFilesObj, stdFilesPriority]);

    // Seed local files once from Redux after back navigation
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (!hydratedRef.current && files.length === 0 && initialStdFiles.length > 0) {
            setFiles(initialStdFiles);
            hydratedRef.current = true;
        }
    }, [initialStdFiles, files.length]);

    const handleFileInput = async (finalFiles) => {
        // Keep changes local; only persist on "Next" button click
        setFiles(finalFiles);
    }

    const getOriginalDataByName = (nameToFind) => {
        const source = files.length > 0 ? files : initialStdFiles;
        const foundObject = source.find(item => (item.fileName || item.name) === nameToFind);
        return foundObject ? (foundObject.fileData?.originalData || foundObject.originalData) : [];
    };

    const handleFileName = (fileName) => {
        setOpen(true);
        setFileName(fileName)
        setChartPreviewData(getOriginalDataByName(fileName))
    }


    // Helper function to compare file arrays for content changes
    const areFilesEqual = (currentFiles, reduxFiles) => {
        if (currentFiles.length !== reduxFiles.length) return false;

        // Compare file names and basic properties (content-wise, order doesn't matter)
        const currentNames = currentFiles.map(f => f.name).sort();
        const reduxNames = reduxFiles.map(f => f.fileName || f.name).sort();

        for (let i = 0; i < currentNames.length; i++) {
            if (currentNames[i] !== reduxNames[i]) return false;
        }

        return true;
    };

    // Helper function to compare file order
    const isOrderEqual = (currentFiles, reduxFiles) => {
        if (currentFiles.length !== reduxFiles.length) return false;

        // Compare file names in their current order
        for (let i = 0; i < currentFiles.length; i++) {
            if (currentFiles[i].name !== (reduxFiles[i].fileName || reduxFiles[i].name)) return false;
        }

        return true;
    };

    const handleNextScreen = async () => {
        const effectiveFiles = files.length > 0 ? files : initialStdFiles;
        const stdFilesPriority = effectiveFiles.map(item => item.fileName || item.name);

        const hasContentChanges = !areFilesEqual(effectiveFiles, initialStdFiles);
        const hasOrderChanges = !isOrderEqual(effectiveFiles, initialStdFiles);

        if (hasContentChanges) {
            dispatch(setStdFilesData(effectiveFiles));
            dispatch(saveStdPriority(stdFilesPriority));

            logUserEvent("upload_std_files", {
                fileCount: effectiveFiles.length,
                fileNames: stdFilesPriority,
                priorityOrder: stdFilesPriority
            });
        } else if (hasOrderChanges) {
            dispatch(saveStdPriority(stdFilesPriority));

            logUserEvent("reorder_std_files", {
                fileCount: effectiveFiles.length,
                newPriorityOrder: stdFilesPriority
            });
        }

        // Saving to Firebase is deferred until UploadUnkFiles step

        navigate('/configuration/uploadQcFiles');
    };


    return (
        <div className=''>
            <ConfirmationModal
                isOpen={showExitModal}
                onClose={handleCancelExit}
                onConfirm={handleConfirmExit}
                title="Leave Configuration?"
                message="If you leave now, the current configuration will not be saved and all progress will be lost. Are you sure you want to continue?"
                confirmText="Leave"
                cancelText="Stay"
                type="warning"
            />
            <div className='flex flex-col px-4 items-center justify-center mt-6'>
                <h2 className='text-lg  mb-8 self-start'>Upload your <span className='font-bold  text-customeOrange'>Standard</span> files by drag and drop or click in the box below.</h2>

            </div>
            <UploadFiles
                initialFiles={files.length > 0 ? files : initialStdFiles}
                moreColors={true}
                showHelpText={(files.length > 1) || (initialStdFiles.length > 1)}
                handleFileInput={handleFileInput}
                fileType={'standard'}
                handleFileName={handleFileName}
                level={true}
                enableReorder={true}
            />
            <div className='fixed bottom-8 right-8 z-40 flex gap-2'>
                <BackButton
                    label="Restart"
                    onBeforeNavigate={async () => {
                        await dispatch(resetAll());
                        // Also clear local list so nothing remains visible on this screen
                        setFiles([]);
                    }}
                    to={'/configuration'}
                />
                <button
                    disabled={(files.length === 0 && initialStdFiles.length === 0)}
                    onClick={() => handleNextScreen()}
                    className={`flex-row flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
                >Next
                    <MdOutlineChevronRight className='ml-6' fontSize={24} />
                </button>
            </div>

            <Modal open={open} onClose={() => setOpen(false)}>
                <LineChartPreview data={chartPreviewData} fileName={fileName} />
            </Modal>
        </div>
    )
}

export default UploadStdFiles